/**
 * Fold a stream of per-file progress ticks into a few applies per second.
 *
 * The backend reports every finished file on its own tick, and each tick
 * used to become a Redux dispatch and a full re-render of the row list — on
 * a 200-file run the renderer was frozen for the whole run in 100–250ms
 * chunks. Non-final ticks are buffered and merged (all their `logs`
 * concatenated, the LAST tick's counters/progress kept, notices joined) and
 * applied once per `flushMs`. A final tick (progress >= 100) flushes whatever
 * is pending first, then goes through untouched, so completion logic sees
 * every row before it runs.
 *
 * `apply` is the original per-tick handler; `merge(batch)` builds the
 * payload for a flush (default: the log-stream shape used by the
 * Compatibility / Loudness / Organize tabs).
 */
export function mergeLogTicks(batch) {
    const last = batch[batch.length - 1];
    const logs = [];
    const notices = [];
    for (const t of batch) {
        if (Array.isArray(t.logs)) logs.push(...t.logs);
        if (t.notice) notices.push(t.notice);
    }
    return { ...last, logs, notice: notices.length ? notices.join('\n') : undefined };
}

export const DEFAULT_FLUSH_MS = 150;

export function batchProgressTicks(apply, {
    flushMs = DEFAULT_FLUSH_MS,
    merge = mergeLogTicks,
    isFinal = (t) => (t.progressPercentage ?? t.progress ?? 0) >= 100,
} = {}) {
    let pending = [];
    let timer = null;
    const flush = () => {
        if (timer) clearTimeout(timer);
        timer = null;
        if (!pending.length) return;
        const batch = pending;
        pending = [];
        apply(merge(batch));
    };
    return (tick) => {
        if (isFinal(tick)) {
            flush();
            return apply(tick);
        }
        pending.push(tick);
        if (!timer) timer = setTimeout(flush, flushMs);
        return undefined;
    };
}
